/**
 * FeaturesAccordion — «Что умеет платформа» в editorial-аккордеоне:
 * нумерованные строки с гигантскими заголовками, раскрытие через
 * details/summary (grid-rows transition в styles.css).
 */
import { Plus } from "lucide-react";
import { useLang } from "../i18n";
import { useReveal } from "../lib/motion";
import { SectionHead } from "./SectionHead";

export function FeaturesAccordion() {
  const { t } = useLang();
  const ref = useReveal<HTMLElement>();

  return (
    <section
      id="features"
      ref={ref}
      className="relative border-t"
      style={{ borderColor: "var(--line)", background: "var(--bg-deep)" }}
    >
      <div
        className="mx-auto w-full max-w-7xl px-5 sm:px-8"
        style={{ paddingTop: "var(--space-section)", paddingBottom: "var(--space-section)" }}
      >
        <SectionHead
          num="04"
          label={t.features.label}
          title={t.features.title}
          accent={t.features.accent}
          aside={t.features.aside}
        />

        <div className="border-t" style={{ borderColor: "var(--line)" }}>
          {t.features.list.map((item, i) => (
            <details
              key={i}
              className="acc reveal group border-b"
              data-reveal=""
              style={{
                borderColor: "var(--line)",
                "--reveal-delay": `${i * 60}ms`,
              } as React.CSSProperties}
            >
              <summary className="flex items-center gap-5 py-6 sm:gap-8 sm:py-8">
                <span
                  className="t-brand w-10 flex-shrink-0 text-sm sm:text-base"
                  style={{ color: "var(--gold)" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-6">
                  <span
                    className="t-display text-2xl transition-colors group-hover:text-[var(--gold-bright)] sm:text-4xl"
                    style={{ color: "var(--fg)" }}
                  >
                    {item.title}
                  </span>
                  <span className="text-sm" style={{ color: "var(--fg-muted)" }}>
                    {item.sub}
                  </span>
                </span>
                <span
                  className="acc-icon flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border"
                  style={{ borderColor: "var(--line-strong)", color: "var(--fg-soft)" }}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                </span>
              </summary>
              <div className="acc-body">
                <div>
                  <div className="grid gap-6 pb-8 pl-[60px] pr-4 sm:grid-cols-[1fr_auto] sm:pl-[72px]">
                    <p
                      className="max-w-2xl text-base leading-relaxed"
                      style={{ color: "var(--fg-soft)" }}
                    >
                      {item.body}
                    </p>
                    <div className="flex flex-wrap items-start gap-2 sm:max-w-[220px] sm:justify-end">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em]"
                          style={{ borderColor: "var(--gold-line)", color: "var(--gold-bright)" }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
